import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import { Box, Stack } from '@mui/material';
import { cyan, indigo } from '@mui/material/colors';
import { addMinutes, differenceInMinutes, subMinutes } from 'date-fns';
import { isEqual, isNil, some } from 'lodash';
import { ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Detection, DetectionClassification, useDetections } from '@/api/historicalData';
import { sensorKey } from '@/api/historicalData/helpers';
import { usePreferredTimeZone } from '@/api/userAccount';
import { dolphinIcon } from '@/assets';
import { Timeline, TimelineChart, drawTimelineChart } from '@/charts/timelineChart';
import { Null } from '@/components/Null';
import { SensorIcon } from '@/components/data/SensorIcon';
import { ChartDataDetails, ChartWithInfo } from '@/components/spotter/historicalDataTab/ChartWithInfo';
import { ReadingSpectrogramChart } from '@/components/spotter/historicalDataTab/SpectrogramSection';
import {
    DetectionDataSummary,
    DetectionDatumDetail,
} from '@/components/spotter/historicalDataTab/datumDetail/DetectionDatumDetail';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useChartUiContext } from '@/context/ChartUiContext';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useChartContainer } from '@/hooks/useChartContainer';
import { useChartInteractionTracking } from '@/hooks/useChartInteractionTracking';
import { useElementSize } from '@/hooks/useElementSize';
import { TrackChartViewWhenRendered } from '@/hooks/useIsViewedTracking';
import { consolidateIntervals } from '@/utils';

const SPL_CONTEXT_WINDOW_MIN_MINS = 10;

const BASE_DETECTIONS_LINE = {
    valueKey: 'density',
    valueRange: { valueMin: 0, valueMax: 100 },
} as const;

// just trying to make Detection look like a GenericObject for the benefit of chart typing
type GenericDetection = Omit<Detection, 'breachedSensorAlert'>;

export const DETECTIONS_SECTIONS: readonly {
    Icon: () => ReactNode;
    line: Timeline<GenericDetection>;
    classification: DetectionClassification;
}[] = [
    {
        // boats will be visually identical to vessels for the bluetide demo
        // spotters will be nicknamed appropriately to track which model we're looking at
        Icon: () => <SensorIcon Icon={DirectionsBoatIcon} />,
        classification: 'boats',
        line: {
            id: 'boat-detections',
            seriesLabel: 'Vessel detections',
            valueLabel: 'Detection confidence',
            color: cyan[800],
            ...BASE_DETECTIONS_LINE,
        },
    },
    {
        Icon: () => <SensorIcon Icon={DirectionsBoatIcon} />,
        classification: 'vessels',
        line: {
            id: 'vessel-detections',
            seriesLabel: 'Vessel detections',
            valueLabel: 'Detection density',
            color: cyan[800],
            ...BASE_DETECTIONS_LINE,
        },
    },
    {
        Icon: () => <SensorIcon src={dolphinIcon} />,
        classification: 'dolphins',
        line: {
            id: 'dolphin-detections',
            seriesLabel: 'Dolphin detections',
            valueLabel: 'Detection density',
            color: indigo[500],
            ...BASE_DETECTIONS_LINE,
        },
    },
] as const;

const shouldShowSection = (section: (typeof DETECTIONS_SECTIONS)[number], detections: Detection[]): boolean =>
    detections.some((detection) => detection.classification === section.classification);

export const DetectionsSection = () => {
    const { spotterId } = useSpotterContext();
    const { dateRange } = useDateRangeContext();

    const { data: detections } = useDetections({
        spotterId: spotterId!,
        dateRange,
    });

    const visibleSections = useMemo(
        () =>
            detections ? DETECTIONS_SECTIONS.filter((sectionProps) => shouldShowSection(sectionProps, detections)) : [],
        [detections]
    );

    if (!detections?.length) {
        return <Null data-testid="detections-section-no-data" />;
    }

    return (
        <>
            {visibleSections.map((sectionProps) => (
                <DetectionChart key={sectionProps.line.id} dataset={detections} {...sectionProps} />
            ))}
        </>
    );
};

export const DetectionChart = ({
    dataset,
    Icon,
    classification,
    line,
}: { dataset: Detection[] } & (typeof DETECTIONS_SECTIONS)[number]) => {
    const { state: locationState } = useLocation();
    const { spotterId } = useSpotterContext();
    const { expandedSubsampleChartId, setExpandedSubsampleChartId } = useChartUiContext();
    const timeZone = usePreferredTimeZone();
    const { dateRange } = useDateRangeContext();
    const { selectedTimestamp, setSelectedTimestamp, setActiveTimeline } = useTimelineNavigationContext();
    const handleUserInteraction = useChartInteractionTracking('Select detection', {
        spotterId,
        detectionClass: classification,
    });
    const [selectedData, setSelectedData] = useState<Detection | null>(null);
    const [splChartRendered, setSplChartRendered] = useState<{ xTimeScale: d3.ScaleTime<number, number, never> }>();
    const chartRef = useRef<TimelineChart<GenericDetection> | null>(null);
    const selectedTimestampRef = useRef(selectedTimestamp);
    selectedTimestampRef.current = selectedTimestamp;

    const containerId = 'detections-chart' + useId();
    const showSplReadings = expandedSubsampleChartId === containerId;
    const { setContainer, containerRef, containerEl } = useChartContainer();
    const sectionRef = useRef<HTMLDivElement>(null);
    const { width } = useElementSize(containerRef);
    const drawChart = useCallback(drawTimelineChart, []);

    const timeResolution = useMemo(() => (some(dataset, { time_resolution: 'high' }) ? 'high' : 'low'), [dataset]);

    const observationIntervals = useMemo(() => {
        // precalculating this rather than letting the chart's dot renderers manage it because we're going to
        // need to get tricky about duty cycles at some point (don't show regular duty cycle off as non-observation,
        // extended gaps)
        return consolidateIntervals({
            dataset,
            dateRangeForDatum: ({ start_time, end_time }) => ({
                startDate: new Date(start_time),
                endDate: new Date(end_time),
            }),
            // not reusing the line's dataFilter because this needs to include non-detections (is_detection: false)
            includeInterval: (datum) =>
                datum.classification === classification && datum.time_resolution === timeResolution,
        });
    }, [classification, dataset, timeResolution]);

    const lines: Timeline<GenericDetection>[] = useMemo(() => {
        return [
            {
                ...line,
                observationIntervals,
                dataFilter: (datum: Detection) =>
                    datum.classification === classification &&
                    datum.time_resolution === timeResolution &&
                    datum.is_detection,
            },
        ];
    }, [classification, line, observationIntervals, timeResolution]);

    /**
     * Duplicate the most recent value of expansionBracketTarget into a reference so we can
     * tell a freshly rendering chart what its value will be without incurring a reactive
     * re-render any time the value changes (instead we use an effect downstream of the
     * memoized showExpansionBracket to imperatively call a chart method.)
     */
    const initialExpansionBracketTarget = useRef<{ xTimeScale: d3.ScaleTime<number, number, never> }>();

    const expansionBracketTarget = useMemo(() => {
        initialExpansionBracketTarget.current = (showSplReadings && splChartRendered) || undefined;
        return initialExpansionBracketTarget.current;
    }, [splChartRendered, showSplReadings]);

    useEffect(() => {
        if (!containerEl || !dataset?.length) {
            return;
        }

        const newChart = drawChart({
            legend: { label: 'Detection density' },
            dataset: dataset as GenericDetection[],
            dateRange,
            xAxisKey: 'start_time',
            xAxisEndKey: 'end_time',
            containerId,
            chartWidth: width,
            chartHeight: 250,
            lines,
            expansionBracketTarget: initialExpansionBracketTarget.current,
            timeZone,
            onSelectTimestamp: (timestamp, timeline) => {
                // doing this inside setExpandedSubsampleChartId so that we don't have dependency on expandedSubsampleChartId which would cause a rerender after
                // every toggle
                setExpandedSubsampleChartId((oldExpandedChartId) => {
                    if (timestamp === null && oldExpandedChartId !== containerId) {
                        // timestamp === null means the currently selected timestamp was clicked again. if we're not already showing readings,
                        // turn them on instead of clearing the cursor
                        setActiveTimeline({ timestamps: timeline! });
                        return containerId;
                    } else {
                        setSelectedTimestamp(timestamp, timeline);
                        return isNil(timestamp) ? null : containerId;
                    }
                });
            },
            onUserInteraction: handleUserInteraction,

            onSelectData: (selected) => {
                if (selected) {
                    setSelectedData(selected[0].datum);
                } else {
                    setSelectedData(null);
                }
            },
        });
        chartRef.current = newChart;
        newChart.setSelectedTimestamp(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);
        newChart.setExpansionBracketTarget(initialExpansionBracketTarget.current);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        containerEl,
        dataset,
        drawChart,
        containerId,
        width,
        timeZone,
        setSelectedTimestamp,
        setActiveTimeline,
        handleUserInteraction,
        lines,
        dateRange,
        expansionBracketTarget,
        setExpandedSubsampleChartId,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    useEffect(() => {
        chartRef.current?.setExpansionBracketTarget(expansionBracketTarget);
    }, [expansionBracketTarget]);

    useEffect(() => {
        if (!selectedTimestamp && expandedSubsampleChartId === containerId) {
            setExpandedSubsampleChartId(null);
        }
    }, [containerId, expandedSubsampleChartId, selectedTimestamp, setExpandedSubsampleChartId]);

    const [navigatedToTimestamp, setNavigatedToTimestamp] = useState(false);
    useEffect(() => {
        if (
            !navigatedToTimestamp &&
            locationState?.detectionClass === classification &&
            locationState?.selectedTimestamp &&
            chartRef.current &&
            containerId &&
            sectionRef.current
        ) {
            setNavigatedToTimestamp(true);
            setSelectedTimestamp(locationState.selectedTimestamp, chartRef.current.allTimestamps);
            setExpandedSubsampleChartId(containerId);
            sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [
        navigatedToTimestamp,
        locationState,
        classification,
        containerId,
        containerEl,
        setSelectedTimestamp,
        selectedTimestamp,
        setExpandedSubsampleChartId,
    ]);

    const splContextDateRange = useMemo(() => {
        if (!selectedData) {
            return null;
        }
        const startDate = new Date(selectedData.start_time);
        const endDate = new Date(selectedData.end_time);

        // if the detection covers more than 10 minutes, show the whole range. if the detection is less than 15 minutes,
        // show a 10 minute window centered on the center of the detection.
        if (differenceInMinutes(endDate, startDate) > SPL_CONTEXT_WINDOW_MIN_MINS) {
            return { startDate, endDate };
        }

        const contextStartTime = subMinutes(
            (startDate.getTime() + endDate.getTime()) / 2,
            SPL_CONTEXT_WINDOW_MIN_MINS / 2
        );
        return { startDate: contextStartTime, endDate: addMinutes(contextStartTime, SPL_CONTEXT_WINDOW_MIN_MINS) };
    }, [selectedData]);

    const handleSplChartRendered = useCallback(
        (hasData: boolean, rendered?: { xTimeScale: d3.ScaleTime<number, number, never> }) => {
            if (!hasData || !rendered) {
                setSplChartRendered(undefined);
                return;
            }
            const oldScale = splChartRendered?.xTimeScale;
            const newScale = rendered?.xTimeScale;
            if (
                oldScale &&
                isEqual(oldScale.domain(), newScale.domain()) &&
                isEqual(oldScale.range(), newScale.range())
            ) {
                return;
            }

            setSplChartRendered(rendered);
        },
        [splChartRendered?.xTimeScale]
    );

    return (
        <CollapsibleSection ref={sectionRef} title={line.seriesLabel} icon={<Icon />}>
            <Stack data-testid={`detection-chart-${classification}`}>
                <Stack direction="row">
                    <TrackChartViewWhenRendered
                        containerEl={containerEl}
                        chartTitle={line.seriesLabel}
                        spotterId={spotterId!}
                    />
                    <ChartWithInfo
                        chartHeight="250px"
                        detailPanelSx={{ height: '168px' }}
                        chart={<Box ref={setContainer} id={containerId} width={1} height={1} />}
                    >
                        <ChartDataDetails>
                            {!selectedData && <DetectionDataSummary dataset={dataset} line={lines[0]} />}
                            {selectedData && (
                                <DetectionDatumDetail data={selectedData} dataset={dataset} line={lines[0]} />
                            )}
                        </ChartDataDetails>
                    </ChartWithInfo>
                </Stack>
                {showSplReadings && selectedData && (
                    <ReadingSpectrogramChart
                        hideLoadingIndicator={
                            /* Don't distract with a loading indicator if we're unlikely to find any readings.  */
                            selectedData.time_resolution === 'low'
                        }
                        dateRange={splContextDateRange!}
                        sensorPosition={sensorKey(selectedData.sensor_position)}
                        openedFrom="detections"
                        onChartRendered={handleSplChartRendered}
                    />
                )}
            </Stack>
        </CollapsibleSection>
    );
};
